import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiErrorMessage } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'

interface CreateCheckoutParams {
  billingId: number
  accountType: string
  teamSize?: number
  billingPackage?: 'monthly' | 'six_month'
  cancelUrl?: string
  onSuccess?: () => void
}

/**
 * Creates a Stripe checkout session and redirects to the payment page
 * @param billingId - The billing account ID
 * @param accountType - The plan type (pro, pro_plus, team, enterprise)
 * @param teamSize - Number of team members (required for team plan)
 * @param billingPackage - Billing package to purchase
 * @param cancelUrl - URL to redirect to if payment is cancelled
 * @param onSuccess - Optional callback before redirect
 * @returns The checkout redirect URL or throws an error
 */
export async function createCheckoutSession({
  billingId,
  accountType,
  teamSize = 1,
  billingPackage = 'monthly',
  cancelUrl,
  onSuccess
}: CreateCheckoutParams): Promise<string> {
  // Handle enterprise separately
  if (accountType === 'enterprise') {
    window.open('https://www.curietech.ai/contact-sales', '_blank')
    onSuccess?.()
    throw new Error('ENTERPRISE_REDIRECT') // Signal that this was handled
  }

  try {
    const checkoutResponse = await fetchWrapper(
      `/api/billing/${billingId}/create-subscription-checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_type: accountType,
          team_size: accountType === 'team' ? teamSize : 1,
          billing_package: billingPackage,
          cancel_url: cancelUrl || `${window.location.origin}/`
        })
      }
    )

    if (!checkoutResponse.ok) {
      const errorData = await checkoutResponse.json().catch(() => ({}))
      throw new Error(
        extractApiErrorMessage(
          errorData,
          `Failed to create checkout session: ${checkoutResponse.status}`
        )
      )
    }

    const checkoutData = await checkoutResponse.json()

    if (!checkoutData.redirect_url) {
      throw new Error('No redirect URL received from checkout session')
    }

    onSuccess?.()
    return checkoutData.redirect_url
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)

    // Don't show error for enterprise redirect
    if (errorMessage !== 'ENTERPRISE_REDIRECT') {
      BetterToast.error(errorMessage)
    }

    throw error
  }
}

/**
 * Creates a checkout session and redirects immediately
 */
export async function createAndRedirectToCheckout(params: CreateCheckoutParams): Promise<void> {
  try {
    const redirectUrl = await createCheckoutSession(params)
    window.location.href = redirectUrl
  } catch (error) {
    // Error already handled in createCheckoutSession
    if (error instanceof Error && error.message === 'ENTERPRISE_REDIRECT') {
      return // Enterprise redirect handled, no error
    }
    throw error
  }
}
